import { useState, useRef, useEffect } from "react";
import { MIN_HEIGHT, MAX_HEIGHT, DEFAULT_HEIGHT } from "@/map/utils/mapConstants";

interface UseMapResizeProps {
  resizable: boolean;
  initialHeight: number;
  storageKey?: string;
}

export const useMapResize = ({ resizable, initialHeight, storageKey }: UseMapResizeProps) => {
  const [containerHeight, setContainerHeight] = useState<number>(() => {
    if (!resizable) return initialHeight;
    const saved = storageKey ? localStorage.getItem(storageKey) : null;
    return saved ? parseInt(saved, 10) : initialHeight;
  });

  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  /**
   * Begin resize drag
   */
  const beginDrag = (e: React.MouseEvent) => {
    if (!resizable) return;
    isDraggingRef.current = true;
    startYRef.current = e.clientY;
    startHeightRef.current = containerHeight;
  };

  /**
   * Set up global mouse event listeners for resizing
   */
  useEffect(() => {
    if (!resizable) return;

    const onMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      const delta = e.clientY - startYRef.current;
      const next = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeightRef.current + delta));
      setContainerHeight(next);
    };

    const onMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        if (storageKey) {
          localStorage.setItem(storageKey, String(containerHeight));
        }
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [containerHeight, resizable, storageKey]);

  return {
    containerHeight,
    beginDrag,
    isDragging: isDraggingRef.current,
  };
};
